import React, { useState, useEffect } from 'react';
import { apiKeyService } from '../services/apiKeyService';
import { useApi } from '../hooks/useApi';
import { Button, Card, LoadingSpinner, Table, Badge, Alert, Modal, Input } from './common';
import { 
  PlusIcon, 
  TrashIcon, 
  EyeIcon, 
  EyeSlashIcon,
  ClipboardDocumentIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const ApiKeyManagement = () => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [newApiKey, setNewApiKey] = useState(null);
  const [showApiKey, setShowApiKey] = useState(false);
  const [revokeConfirm, setRevokeConfirm] = useState(null);
  const [revokeAllConfirm, setRevokeAllConfirm] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    keyName: '',
    permissions: 'links:read,links:write',
    expiresInDays: ''
  });

  // API calls
  const { 
    loading: keysLoading, 
    error: keysError, 
    executeApiCall: executeGetKeys 
  } = useApi();

  const { 
    loading: usageLoading, 
    error: usageError, 
    executeApiCall: executeGetUsage 
  } = useApi();

  const { 
    executeApiCall: executeCreateApiKey, 
    loading: createLoading, 
    error: createError 
  } = useApi();

  const { 
    executeApiCall: executeRevokeApiKey, 
    loading: revokeLoading, 
    error: revokeError 
  } = useApi();

  const { 
    executeApiCall: executeRevokeAllApiKeys, 
    loading: revokeAllLoading, 
    error: revokeAllError 
  } = useApi();

  // State for API data
  const [apiKeys, setApiKeys] = useState(null);
  const [usageData, setUsageData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      console.log('Fetching API key data...');
      
      const keysResult = await executeGetKeys(apiKeyService.getApiKeys);
      console.log('Keys result:', keysResult);
      if (keysResult.success) {
        console.log('Setting apiKeys to:', keysResult.data);
        setApiKeys(keysResult.data);
      }
      
      const usageResult = await executeGetUsage(apiKeyService.getApiKeyUsage);
      console.log('Usage result:', usageResult);
      if (usageResult.success) {
        setUsageData(usageResult.data);
      }
    };
    
    fetchData();
  }, [executeGetKeys, executeGetUsage]);

  const handleCreateApiKey = async () => {
    try {
      const result = await executeCreateApiKey(apiKeyService.createApiKey, formData);
      if (result.success) {
        setNewApiKey(result.data);
        setShowCreateModal(false);
        setShowApiKeyModal(true);
        setFormData({ keyName: '', permissions: 'links:read,links:write', expiresInDays: '' });
        
        // Refresh the data
        const keysResult = await executeGetKeys(apiKeyService.getApiKeys);
        if (keysResult.success) {
          setApiKeys(keysResult.data);
        }
        
        const usageResult = await executeGetUsage(apiKeyService.getApiKeyUsage);
        if (usageResult.success) {
          setUsageData(usageResult.data);
        }
      }
    } catch (error) {
      console.error('Error creating API key:', error);
    }
  };

  const handleRevokeApiKey = async (id) => {
    try {
      const result = await executeRevokeApiKey(apiKeyService.revokeApiKey, id);
      if (result.success) {
        setRevokeConfirm(null);
        
        // Refresh the data
        const keysResult = await executeGetKeys(apiKeyService.getApiKeys);
        if (keysResult.success) {
          setApiKeys(keysResult.data);
        }
        
        const usageResult = await executeGetUsage(apiKeyService.getApiKeyUsage);
        if (usageResult.success) {
          setUsageData(usageResult.data);
        }
      }
    } catch (error) {
      console.error('Error revoking API key:', error);
    }
  };

  const handleRevokeAllApiKeys = async () => {
    try {
      const result = await executeRevokeAllApiKeys(apiKeyService.revokeAllApiKeys);
      if (result.success) {
        setRevokeAllConfirm(false);
        
        // Refresh the data
        const keysResult = await executeGetKeys(apiKeyService.getApiKeys);
        if (keysResult.success) {
          setApiKeys(keysResult.data);
        }
        
        const usageResult = await executeGetUsage(apiKeyService.getApiKeyUsage);
        if (usageResult.success) {
          setUsageData(usageResult.data);
        }
      }
    } catch (error) {
      console.error('Error revoking all API keys:', error);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  const getPermissionBadgeColor = (permissions) => {
    if (permissions.includes('*')) return 'bg-purple-100 text-purple-800';
    if (permissions.includes('links:write')) return 'bg-blue-100 text-blue-800';
    return 'bg-ink-100 text-ink-800';
  };

  const getStatusBadge = (isActive, expiresAt) => {
    if (!isActive) return <Badge variant="danger">Revoked</Badge>;
    if (expiresAt && new Date(expiresAt) < new Date()) {
      return <Badge variant="warning">Expired</Badge>;
    }
    return <Badge variant="success">Active</Badge>;
  };

  const Header = (
    <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-ink-900">
          API keys
        </h1>
        <p className="mt-1.5 text-sm text-ink-500">
          Programmatic access to your links and profile pages.
        </p>
      </div>
      <Button variant="primary" size="medium" onClick={() => setShowCreateModal(true)}>
        <PlusIcon className="w-4 h-4" />
        New API key
      </Button>
    </header>
  );

  if (keysLoading || usageLoading) {
    return (
      <div className="max-w-6xl mx-auto">
        {Header}
        <LoadingSpinner text="Loading API keys…" />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {Header}

      {/* Usage Statistics */}
      <div className="grid grid-cols-3 gap-px rounded-2xl border border-ink-200 bg-ink-100 overflow-hidden shadow-soft mb-6">
        {[
          { label: 'Total keys', value: usageData?.data?.totalKeys || 0, color: 'text-brand-600' },
          { label: 'Active keys', value: usageData?.data?.activeKeys || 0, color: 'text-emerald-600' },
          { label: 'Expired keys', value: usageData?.data?.expiredKeys || 0, color: 'text-amber-600' },
        ].map((s) => (
          <div key={s.label} className="bg-white px-5 py-4">
            <p className="text-xs uppercase tracking-wider text-ink-500">{s.label}</p>
            <p className={`mt-1 text-2xl font-bold tracking-tight ${s.color}`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* API Keys List */}
      <Card
        title="Your API keys"
        subtitle="Each key has its own permissions and can be revoked independently."
        actions={
          (apiKeys?.data?.length > 0 || apiKeys?.length > 0) ? (
            <Button onClick={() => setRevokeAllConfirm(true)} variant="danger" size="small">
              <TrashIcon className="w-4 h-4" />
              Revoke all
            </Button>
          ) : null
        }
      >

        {(keysError || usageError) && (
          <Alert 
            type="error" 
            title="Error Loading Data" 
            message={keysError?.message || usageError?.message || 'Failed to load API key data'} 
          />
        )}

        {!apiKeys ? (
          <div className="py-10 text-center text-sm text-ink-500">
            Loading API keys…
          </div>
        ) : (apiKeys?.data?.length === 0 || apiKeys?.length === 0) ? (
          <div className="text-center py-10">
            <div className="mx-auto mb-4 inline-flex items-center justify-center h-12 w-12 rounded-xl bg-brand-50 text-brand-600 ring-1 ring-brand-200/60">
              <ExclamationTriangleIcon className="w-6 h-6" />
            </div>
            <h3 className="text-base font-semibold text-ink-900">No API keys yet</h3>
            <p className="mt-1 text-sm text-ink-500">Create one to start using the REST API.</p>
            <Button className="mt-5" onClick={() => setShowCreateModal(true)}>
              <PlusIcon className="w-4 h-4" />
              Create API key
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Header>Key Name</Table.Header>
                  <Table.Header>Permissions</Table.Header>
                  <Table.Header>Status</Table.Header>
                  <Table.Header>Requests</Table.Header>
                  <Table.Header>Last Used</Table.Header>
                  <Table.Header>Created</Table.Header>
                  <Table.Header>Actions</Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {(apiKeys?.data || apiKeys)?.map((key) => (
                  <Table.Row key={key.id}>
                    <Table.Cell>
                      <div className="font-medium text-ink-900">{key.keyName}</div>
                    </Table.Cell>
                    <Table.Cell>
                      <Badge variant="primary">
                        {key.permissions}
                      </Badge>
                    </Table.Cell>
                    <Table.Cell>
                      {getStatusBadge(key.isActive, key.expiresAt)}
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm font-medium text-ink-700">
                        {(key.requestCount || 0).toLocaleString()}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-ink-600">
                        {formatDate(key.lastUsedAt)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <span className="text-sm text-ink-600">
                        {formatDate(key.createdAt)}
                      </span>
                    </Table.Cell>
                    <Table.Cell>
                      <div className="flex space-x-2">
                        {key.isActive && (
                          <Button
                            onClick={() => setRevokeConfirm(key)}
                            variant="danger"
                            size="small"
                          >
                            <TrashIcon className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        )}
      </Card>

      {/* Create API Key Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create New API Key"
        size="medium"
      >
        <div className="space-y-4">
          {createError && (
            <Alert type="error" title="Error" message={createError.message} />
          )}

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              Key Name *
            </label>
            <Input
              type="text"
              value={formData.keyName}
              onChange={(e) => setFormData({ ...formData, keyName: e.target.value })}
              placeholder="e.g., Production API Key"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              Permissions
            </label>
            <select
              value={formData.permissions}
              onChange={(e) => setFormData({ ...formData, permissions: e.target.value })}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm text-ink-900 focus:outline-none focus:ring-4 focus:ring-brand-100 focus:border-brand-500"
            >
              <option value="links:read,links:write">Links: Read & Write</option>
              <option value="links:read">Links: Read Only</option>
              <option value="links:read,links:write,links:delete">Links: Full Access</option>
              <option value="links:read,links:write,profile:read,profile:write">Links & Profiles: Full Access</option>
              <option value="links:read,links:write,links:delete,profile:read,profile:write">Everything (all permissions)</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              Expires In (Days) - Optional
            </label>
            <Input
              type="number"
              value={formData.expiresInDays}
              onChange={(e) => setFormData({ ...formData, expiresInDays: e.target.value })}
              placeholder="Leave empty for no expiration"
              min="1"
            />
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setShowCreateModal(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateApiKey}
            loading={createLoading}
            disabled={!formData.keyName.trim()}
          >
            Create API Key
          </Button>
        </div>
      </Modal>

      {/* Show New API Key Modal */}
      <Modal
        isOpen={showApiKeyModal}
        onClose={() => setShowApiKeyModal(false)}
        title="API Key Created Successfully"
        size="medium"
      >
        <div className="space-y-4">
          <Alert type="warning" title="Important" message="This is the only time you'll see your API key. Please copy it and store it securely." />
          
          <div>
            <label className="block text-sm font-medium text-ink-700 mb-1">
              Your API Key
            </label>
            <div className="relative">
              <Input
                type={showApiKey ? "text" : "password"}
                value={newApiKey ? newApiKey.apiKey : ''}
                readOnly
                className="pr-12"
              />
              <div className="absolute inset-y-0 right-0 flex items-center space-x-1 pr-3">
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => setShowApiKey(!showApiKey)}
                >
                  {showApiKey ? <EyeSlashIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="small"
                  onClick={() => copyToClipboard(newApiKey ? newApiKey.apiKey : '')}
                >
                  <ClipboardDocumentIcon className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-ink-200 bg-ink-900 text-ink-100 p-4">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-ink-400 mb-2">Usage example</h4>
            <pre className="text-xs leading-relaxed font-mono whitespace-pre-wrap text-ink-100">
{`curl -X POST "http://localhost:4002/api/v1/links" \\
  -H "Content-Type: application/json" \\
  -H "X-API-Key: ${newApiKey ? newApiKey.apiKey : 'YOUR_API_KEY'}" \\
  -d '{"originalUrl": "https://example.com", "title": "My Link"}'`}
            </pre>
          </div>
        </div>

        <div className="flex justify-end mt-6">
          <Button onClick={() => setShowApiKeyModal(false)}>
            Got it!
          </Button>
        </div>
      </Modal>

      {/* Revoke Confirmation Modal */}
      <Modal
        isOpen={!!revokeConfirm}
        onClose={() => setRevokeConfirm(null)}
        title="Revoke API Key"
        size="small"
      >
        <div className="space-y-4">
          <p className="text-ink-600">
            Are you sure you want to revoke the API key "{revokeConfirm?.keyName}"?
            This action cannot be undone.
          </p>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setRevokeConfirm(null)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => handleRevokeApiKey(revokeConfirm?.id)}
            loading={revokeLoading}
          >
            Revoke key
          </Button>
        </div>
      </Modal>

      {/* Revoke All Confirmation Modal */}
      <Modal
        isOpen={revokeAllConfirm}
        onClose={() => setRevokeAllConfirm(false)}
        title="Revoke All API Keys"
        size="small"
      >
        <div className="space-y-4">
          <Alert type="error" title="Warning" message="This will revoke ALL your API keys. This action cannot be undone." />
          <p className="text-ink-600">
            Are you sure you want to revoke all API keys? You'll need to create new ones to continue using the API.
          </p>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <Button
            variant="outline"
            onClick={() => setRevokeAllConfirm(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleRevokeAllApiKeys}
            loading={revokeAllLoading}
          >
            Revoke all keys
          </Button>
        </div>
      </Modal>
    </div>
  );
};

export default ApiKeyManagement;
